package com.presentocracy.shared.model

import kotlinx.serialization.Serializable

@Serializable
enum class BaseVisibility {
    ONLY_ME,
    FRIENDS,
    SELECTED_PEOPLE,
    EVERYONE,
    /** @deprecated Use EVERYONE in new requests. Kept for persisted v1 rows. */
    PUBLIC,
}

@Serializable
enum class AccessOverride {
    ALLOW,
    DENY,
}

@Serializable
enum class ReservationVisibility {
    HIDDEN,
    STATUS_ONLY,
    FULL,
}

@Serializable
enum class GiftPriority {
    LOW,
    NORMAL,
    HIGH,
    VERY_HIGH,
    DREAM,
}

@Serializable
enum class GiftStatus {
    ACTIVE,
    FULFILLED,
    ARCHIVED,
}

@Serializable
enum class ReservationStatus {
    ACTIVE,
    CANCELLED,
    PURCHASED,
    GIFTED,
}

@Serializable
data class ReservationInfo(
    val status: ReservationStatus,
    val reservedByUserId: String? = null,
    val reservedByDisplayName: String? = null,
)

@Serializable
enum class FulfilledGiftBehavior {
    KEEP_IN_PLACE,
    MOVE_TO_BOTTOM,
    MOVE_TO_FULFILLED,
    /** @deprecated Legacy display mode retained for existing rows. */
    HIDE,
    /** @deprecated Legacy display mode retained for existing rows. */
    DIM,
    /** @deprecated Legacy display mode retained for existing rows. */
    KEEP,
}

@Serializable
enum class WishlistSortMode {
    MANUAL,
    PRIORITY,
    PRICE_ASC,
    PRICE_DESC,
    DATE_ADDED,
}

@Serializable
enum class FriendshipStatus {
    PENDING,
    ACCEPTED,
    DECLINED,
    BLOCKED,
}

@Serializable
data class UserCard(
    val id: String,
    val username: String,
    val displayName: String,
    val avatarUrl: String? = null,
)

@Serializable
data class Friendship(
    val id: String,
    val user: UserCard,
    val status: FriendshipStatus,
    val isIncoming: Boolean = false,
    /** Viewer-authorized count, never an owner's total. Null on old servers/non-friend rows. */
    val visibleWishlistCount: Int? = null,
) {
    init { require(visibleWishlistCount == null || visibleWishlistCount >= 0) }
}

@Serializable
data class Dossier(
    val id: String,
    val ownerId: String,
    val subjectUserId: String,
    val dataJson: String,
    val baseVisibility: BaseVisibility = BaseVisibility.ONLY_ME,
)

@Serializable
data class BirthdaySummary(
    val user: UserCard,
    val birthday: Birthday,
    val daysUntil: Int,
)

@Serializable
data class ReminderSettings(
    val enabled: Boolean = true,
    val offsetsDays: List<Int> = listOf(7, 1),
)

@Serializable
data class NotificationItem(
    val id: String,
    val kind: String,
    val title: String,
    val body: String,
    val createdAt: Long,
    val readAt: Long? = null,
)

@Serializable
data class Wishlist(
    val id: String,
    val ownerId: String,
    val slug: String,
    val name: String,
    val description: String? = null,
    val baseVisibility: BaseVisibility = BaseVisibility.ONLY_ME,
    val reservationVisibility: ReservationVisibility = ReservationVisibility.HIDDEN,
    val fulfilledGiftBehavior: FulfilledGiftBehavior = FulfilledGiftBehavior.MOVE_TO_BOTTOM,
    val sortMode: WishlistSortMode = WishlistSortMode.MANUAL,
    val gifts: List<Gift> = emptyList(),
)

@Serializable
data class Gift(
    val id: String,
    val wishlistId: String,
    val title: String,
    val description: String? = null,
    val note: String? = null,
    val productUrl: String? = null,
    val imageId: String? = null,
    val priceMinorUnits: Long? = null,
    val priceCurrency: String? = null,
    val priority: GiftPriority = GiftPriority.NORMAL,
    val quantity: Int = 1,
    val position: Long = 0,
    val status: GiftStatus = GiftStatus.ACTIVE,
    val completedAt: Long? = null,
    val attributesJson: String? = null,
    val reservation: ReservationInfo? = null,
)

data class AccessContext(
    val ownerId: String,
    val viewerId: String?,
    val baseVisibility: BaseVisibility,
    val override: AccessOverride? = null,
    val isFriend: Boolean = false,
)

fun AccessContext.canView(): Boolean {
    if (viewerId == ownerId) return true
    if (override == AccessOverride.DENY) return false
    if (override == AccessOverride.ALLOW) return true
    return when (baseVisibility) {
        BaseVisibility.ONLY_ME, BaseVisibility.SELECTED_PEOPLE -> false
        BaseVisibility.FRIENDS -> isFriend
        BaseVisibility.EVERYONE, BaseVisibility.PUBLIC -> true
    }
}
